import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { StarRating } from './StarRating';
import { MapPin, CheckCircle, Dog, Cat } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { getServiceName, formatPrice, getCityName } from '../lib/constants';

export function ProviderCard({ provider, compact = false }) {
  const user = provider.user || {};
  const services = provider.services || [];
  const zones = provider.zones || [];
  
  const minPrice = services.length > 0 
    ? Math.min(...services.map(s => s.price_per_hour || s.price_per_day || 0))
    : null;

  if (compact) {
    return (
      <Link to={`/client/providers/${provider.provider_id}`} data-testid={`provider-card-${provider.provider_id}`}>
        <Card className="provider-card cursor-pointer">
          <CardContent className="p-4 flex items-center gap-4">
            <Avatar className="h-14 w-14 border-2 border-primary/20">
              <AvatarImage src={user.picture} alt={user.name} />
              <AvatarFallback>{user.name?.charAt(0) || 'P'}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold truncate">{user.name}</h3>
                {provider.verified && (
                  <CheckCircle weight="fill" className="w-4 h-4 text-primary flex-shrink-0" />
                )}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <StarRating rating={provider.rating || 0} size="sm" showCount count={provider.review_count || 0} />
              </div>
              <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                <MapPin className="w-3.5 h-3.5" />
                <span className="truncate">{zones.map(getCityName).join(', ') || 'Portugal'}</span>
              </div>
            </div>
            {minPrice && (
              <div className="text-right">
                <p className="text-sm text-muted-foreground">desde</p>
                <p className="font-semibold text-primary">{formatPrice(minPrice)}/h</p>
              </div>
            )}
          </CardContent>
        </Card>
      </Link>
    );
  }

  return (
    <Link to={`/client/providers/${provider.provider_id}`} data-testid={`provider-card-${provider.provider_id}`}>
      <Card className="provider-card cursor-pointer overflow-hidden">
        <div className="relative h-32 bg-gradient-to-br from-primary/20 to-secondary/20">
          {provider.photos?.[0] && (
            <img 
              src={provider.photos[0]} 
              alt="" 
              className="w-full h-full object-cover"
            />
          )}
          <Avatar className="absolute -bottom-6 left-4 h-16 w-16 border-4 border-background">
            <AvatarImage src={user.picture} alt={user.name} />
            <AvatarFallback>{user.name?.charAt(0) || 'P'}</AvatarFallback>
          </Avatar>
        </div>
        <CardContent className="pt-8 pb-4 px-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-lg">{user.name}</h3>
                {provider.verified && (
                  <CheckCircle weight="fill" className="w-5 h-5 text-primary" />
                )}
              </div>
              <StarRating rating={provider.rating || 0} size="sm" showCount count={provider.review_count || 0} />
            </div>
            {minPrice && (
              <div className="text-right">
                <p className="text-xs text-muted-foreground">desde</p>
                <p className="font-bold text-primary">{formatPrice(minPrice)}/h</p>
              </div>
            )}
          </div>
          
          {provider.bio && (
            <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{provider.bio}</p>
          )}
          
          <div className="flex items-center gap-1 text-sm text-muted-foreground mt-3">
            <MapPin className="w-4 h-4" />
            <span>{zones.map(getCityName).join(', ') || 'Portugal'}</span>
          </div>
          
          <div className="flex flex-wrap gap-1.5 mt-3">
            {services.slice(0, 3).map((service) => (
              <Badge key={service.type} variant="secondary" className="text-xs">
                {getServiceName(service.type)}
              </Badge>
            ))}
            {services.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{services.length - 3}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
